import {
  type AutocompleteProps,
  type ComboboxProps,
  Stack,
  Text,
} from "@mantine/core"
import type { UseFormReturnType } from "@mantine/form"
import { useCallback, useRef, type ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { LabelWithTooltip, LazyAutocomplete } from "@tfg/ui"

import { findBusinessPartnerRepositoryCompanies } from "~/gen/api/hooks/useFindBusinessPartnerRepositoryCompanies.ts"
import type { BusinessPartnerRepositorySuggestionResponseDto } from "~/gen/api/types/BusinessPartnerRepositorySuggestionResponseDto.ts"
import { BusinessPartnerRepositorySuggestionOption } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/BusinessPartnerRepositorySuggestionOption.tsx"
import { useBusinessPartnerRepositoryAutocompleteIdleBlur } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/useBusinessPartnerRepositoryAutocompleteIdleBlur.ts"
import {
  useBusinessPartnerRepositoryAutocompletePrependOptions,
  type BusinessPartnerCreateNewRowOption,
  type BpRepoSuggestionAutocompleteOption,
} from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/useBusinessPartnerRepositoryAutocompletePrependOptions.ts"
import { useOnAutocompleteValueChanged } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/useOnAutocompleteValueChanged.ts"
import { usePrefill } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/usePrefill.ts"
import {
  BP_REPOSITORY_AUTOCOMPLETE_DROPDOWN_LIMIT,
  doesBusinessPartnerRepositorySuggestionMatchSearch,
  findPrimaryVisibleStrongSuggestionForSearch,
  isStrongBusinessPartnerRepositorySuggestion,
  MAX_BP_REPOSITORY_AUTOCOMPLETE_DIRECTORY_MATCHES,
  MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS,
} from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/utils.ts"
import { getNestedError } from "~/pages/Admin/components/Fields/utils"
import { FormFieldConfig } from "~/pages/Admin/components/form.types"
import { errorCodes } from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap"
import { getNestedValue } from "~/utils/types"

export type BusinessPartnerOrganizationAutocompleteFieldProps = {
  readonly field: FormFieldConfig
  readonly form: UseFormReturnType<Record<string, unknown>>
  readonly error?: keyof errorCodes
  readonly translations?: errorCodes
}

const COMBOBOX_PROPS: ComboboxProps = {
  withinPortal: true,
}

type BusinessPartnerSuggestionOption = BpRepoSuggestionAutocompleteOption

type BusinessPartnerAutocompleteOption =
  | BusinessPartnerCreateNewRowOption
  | BusinessPartnerSuggestionOption

export const BusinessPartnerOrganizationAutocompleteField = ({
  field,
  form,
}: BusinessPartnerOrganizationAutocompleteFieldProps): ReactElement => {
  const { t } = useTranslation("traceability", {
    keyPrefix: "contactModal.businessPartnerRepository",
  })
  const errors = getNestedError(form.errors, field.name)

  const nameValueRaw = getNestedValue(form.values, field.name)
  const nameValue = typeof nameValueRaw === "string" ? nameValueRaw : ""

  const toSuggestionOption = useCallback(
    (
      suggestion: BusinessPartnerRepositorySuggestionResponseDto
    ): BusinessPartnerSuggestionOption => ({
      value: suggestion.repositoryRecordId,
      label: suggestion.name,
      suggestedBusinessPartner: suggestion,
    }),
    []
  )

  const isBusinessPartnerSuggestionOption = useCallback(
    (option: unknown): option is BusinessPartnerSuggestionOption => {
      if (typeof option !== "object" || option === null) {
        return false
      }
      return Reflect.has(option, "suggestedBusinessPartner")
    },
    []
  )

  const isCreateAnywayOption = useCallback(
    (option: unknown): option is BusinessPartnerCreateNewRowOption => {
      if (typeof option !== "object" || option === null) {
        return false
      }
      return Reflect.get(option, "kind") === "createAnyway"
    },
    []
  )

  const { prependOptions } =
    useBusinessPartnerRepositoryAutocompletePrependOptions()

  const optionsFilter: NonNullable<AutocompleteProps["filter"]> = useCallback(
    ({ options, search }) => {
      const q = search.toLowerCase().trim()
      if (q.length === 0) {
        return []
      }
      const trimmedSearchLength = search.trim().length
      const suggestionOptions = options.filter(
        isBusinessPartnerSuggestionOption
      )
      const filteredSuggestions = suggestionOptions.filter((option) =>
        doesBusinessPartnerRepositorySuggestionMatchSearch(
          option.suggestedBusinessPartner,
          search
        )
      )
      const hasStrongDirectoryMatch = filteredSuggestions.some((option) =>
        isStrongBusinessPartnerRepositorySuggestion(
          option.suggestedBusinessPartner
        )
      )
      const createAnywayRows =
        trimmedSearchLength >= MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS &&
        hasStrongDirectoryMatch
          ? options.filter(isCreateAnywayOption)
          : []
      return [...createAnywayRows, ...filteredSuggestions]
    },
    [isBusinessPartnerSuggestionOption, isCreateAnywayOption]
  )

  const {
    onOptionSubmit,
    linkedRepositoryCompanyNameRef,
    abortDirectoryPrefillAndResetOrganizationFields,
  } = usePrefill({
    form,
    onBusinessPartnerRepositorySelected:
      field.onBusinessPartnerRepositorySelected,
  })

  const {
    onAutocompleteValueChange,
    onAutocompleteClear,
    onCreateNewWithoutDirectorySelection,
  } = useOnAutocompleteValueChanged({
    form,
    organizationNameFieldName: field.name,
    linkedRepositoryCompanyNameRef,
    abortDirectoryPrefillAndResetOrganizationFields,
    onBusinessPartnerRepositorySelectionCleared:
      field.onBusinessPartnerRepositorySelectionCleared,
    onDuplicateCandidateForCreateNew: field.onDuplicateCandidateForCreateNew,
  })

  const renderOption: AutocompleteProps["renderOption"] = useCallback(
    ({
      option,
    }: Parameters<NonNullable<AutocompleteProps["renderOption"]>>[0]) => {
      if (isCreateAnywayOption(option)) {
        const term = option.label
        return (
          <Text size="sm" fw={600} lineClamp={2} py={2}>
            {t("createNewOption", { term })}
          </Text>
        )
      }
      if (isBusinessPartnerSuggestionOption(option)) {
        return (
          <BusinessPartnerRepositorySuggestionOption
            suggestion={option.suggestedBusinessPartner}
          />
        )
      }
      const optionLabel = Reflect.get(option, "label")
      const optionValue = Reflect.get(option, "value")
      const fallbackLabel =
        typeof optionLabel === "string"
          ? optionLabel
          : typeof optionValue === "string"
            ? optionValue
            : ""
      return <Text size="sm">{fallbackLabel}</Text>
    },
    [isBusinessPartnerSuggestionOption, isCreateAnywayOption, t]
  )

  const fetchPage = useCallback(async ({ search }: { search: string }) => {
    const response = await findBusinessPartnerRepositoryCompanies({
      term: search,
      limit: MAX_BP_REPOSITORY_AUTOCOMPLETE_DIRECTORY_MATCHES,
    })

    return {
      items: response.data.organizations ?? [],
    }
  }, [])

  const organizationNameInputRef = useRef<HTMLInputElement>(null)

  const findFirstStrongSuggestion = useCallback(
    (
      remoteOptions: readonly unknown[],
      search: string
    ): BusinessPartnerRepositorySuggestionResponseDto | null => {
      const suggestionsInDisplayOrder: BusinessPartnerRepositorySuggestionResponseDto[] =
        []
      for (const item of remoteOptions) {
        if (!isBusinessPartnerSuggestionOption(item)) {
          continue
        }
        suggestionsInDisplayOrder.push(item.suggestedBusinessPartner)
      }
      return findPrimaryVisibleStrongSuggestionForSearch(
        suggestionsInDisplayOrder,
        search
      )
    },
    [isBusinessPartnerSuggestionOption]
  )

  const getCreateNewChosenName = useCallback(
    (option: unknown): string | null => {
      if (!isCreateAnywayOption(option)) {
        return null
      }
      return option.label
    },
    [isCreateAnywayOption]
  )

  const getCommittedSuggestionIfAny = useCallback(
    (
      option: unknown
    ): BusinessPartnerRepositorySuggestionResponseDto | null => {
      if (!isBusinessPartnerSuggestionOption(option)) {
        return null
      }
      return option.suggestedBusinessPartner
    },
    [isBusinessPartnerSuggestionOption]
  )

  const {
    outsideSubmitSyncRef,
    handleAutocompleteFocus,
    handleAutocompleteClear,
    handleComboboxIdleBlur,
    onOptionSelected: onAutocompleteOptionSelected,
  } = useBusinessPartnerRepositoryAutocompleteIdleBlur({
    linkedRepositoryCompanyNameRef,
    onOptionSubmit,
    onAutocompleteClear,
    onCreateNewWithoutDirectorySelection,
    organizationNameInputRef,
    findFirstStrongSuggestion,
    getCreateNewChosenName,
    getCommittedSuggestionIfAny,
  })

  return (
    <Stack gap="var(--mantine-spacing-xxs)">
      {field.tooltip ? (
        <LabelWithTooltip
          label={field.label}
          hoverHelperText={field.tooltip}
          justify="space-between"
          fw={600}
          withAsterisk={field.required ?? false}
        />
      ) : null}
      <LazyAutocomplete<
        BusinessPartnerRepositorySuggestionResponseDto,
        BusinessPartnerAutocompleteOption
      >
        fetchPage={fetchPage}
        toOption={toSuggestionOption}
        prependOptions={prependOptions}
        inputRef={organizationNameInputRef}
        outsideSubmitSyncRef={outsideSubmitSyncRef}
        onComboboxIdleBlur={handleComboboxIdleBlur}
        onFocus={handleAutocompleteFocus}
        debounceMs={300}
        minChars={MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS}
        fetchOnFocus={false}
        size="xs"
        initialSearchValue={nameValue}
        onDebouncedSearchValueChange={onAutocompleteValueChange}
        onOptionSelected={onAutocompleteOptionSelected}
        onClear={handleAutocompleteClear}
        filter={optionsFilter}
        renderOption={renderOption}
        label={field.tooltip ? undefined : field.label}
        placeholder={field.placeholder}
        clearable={field.clearable ?? true}
        error={errors}
        disabled={field.disabled}
        limit={BP_REPOSITORY_AUTOCOMPLETE_DROPDOWN_LIMIT}
        comboboxProps={COMBOBOX_PROPS}
      />
    </Stack>
  )
}
